import { appConfig } from "@/constants";
import {
  type DiscoverFeedItem,
  type TemplateTypes,
  getDiscoverFeedItemFromFieldData,
} from "@/domain/gems-game/domain";
import { isMobile } from "@/utils/browser";
import { notifyError } from "@/utils/monitoring/newrelic";
import {
  DiscordConnectionItem,
  XConnectionItem,
  allCaughtUpCard,
} from "@components/Discover/utils";
import {
  type DiscoverFeedFieldData,
  EarningType,
  type Platform,
  Quest,
  type QuestSection,
  QuestTileType,
  type RuleId,
  VideoUrl,
} from "@imx-tokens-ts/gems-game/domain";
import { match } from "ts-pattern";
import { getFeedTypes } from "./discover-feed-types";
import type { InjectableFetch } from "./types";

export type FieldDataQuest = {
  manual: boolean;
  "gem-distribution-pending": boolean;
  "are-gems-distributed": boolean;
  "background-video"?: string;
  "game-link"?: string;
  "apple-store-game-link"?: string;
  "google-play-game-link"?: string;
  "layer3-link"?: string;
  "register-link"?: string;
  "start-date"?: string;
  "end-date"?: string;
  "discord-link"?: string;
  "youtube-link"?: string;
  "twitter-link"?: string;
  "support-link"?: string;
  "trading-link"?: string;
  "quiz-link"?: string;
  "verify-eligibility-link"?: string;
  "supported-platforms"?: string[];
  "gems-amount"?: number;
  "analytics-id": string;
  "game-id": string;
  "in-game-quest"?: boolean;
  type: string;
  name: string;
  slug: string;
  "estimated-completion-time"?: string;
  rules?: string;
  "custom-title"?: string;
  "custom-coming-soon-title"?: string;
  "sub-text"?: string;
  logo: {
    fileId: string;
    url: string;
    alt: string | null;
  };
  "background-image": {
    fileId: string;
    url: string;
    alt: string | null;
  };
  "dedicated-quest-page-id"?: string;
  order: number;
  cta?: string;
  "cta-icon"?: string;
};

export type FieldDataQuestSection = {
  name: string;
  description?: string;
  "quest-ids": string;
  order: number;
  promoted: boolean;
  slug: string;
};

export type Item<T> = {
  id: string;
  cmsLocaleId: string;
  lastPublished: string | null;
  lastUpdated: string;
  createdOn: string;
  isArchived: boolean;
  isDraft: boolean;
  fieldData: T;
};

export type Response<T> = {
  items: Item<T>[];
  pagination?: {
    limit: number;
    offset: number;
    total: number;
  };
};

export type GameTile = {
  id: string;
  caption: string;
  title: string;
  description: string;
  logo: string;
  link: string;
  isTrusted: boolean; // Should be used to set the rel and referrer-policy attributes of links. Trusted === sites which Immutable has some control over (e.g. Toolkit)
  buttonText: string;
};

function constructUrl(url: string | undefined): URL | undefined {
  return url ? new URL(url) : undefined;
}

type GetQuestsResponse = {
  featuredQuests: FieldDataQuest[];
  mainQuests: FieldDataQuest[];
  sideQuests: FieldDataQuest[];
  onboardingQuests: FieldDataQuest[];
  seriesQuests: FieldDataQuest[];
  inGameQuests: FieldDataQuest[];
};

export const getQuests = async (
  fetch: InjectableFetch,
  collectionItemsUrl: URL,
  accumulatedQuests: Item<FieldDataQuest>[] = [],
): Promise<GetQuestsResponse> => {
  const res = await fetch<Response<FieldDataQuest>, Error>(
    `${collectionItemsUrl.toString()}?offset=${accumulatedQuests.length}&limit=100`,
  );

  const response: GetQuestsResponse = {
    featuredQuests: [],
    mainQuests: [],
    sideQuests: [],
    onboardingQuests: [],
    seriesQuests: [],
    inGameQuests: [],
  };

  return await match(res)
    .with({ ok: true }, async (res) => {
      const body = await res.json();

      const questItems = [...accumulatedQuests, ...body.items];

      // If we have 100 items in the fetch response, we need to fetch the next page
      if (body.items.length === 100) {
        return getQuests(fetch, collectionItemsUrl, questItems);
      }

      const sortedCmsQuests = [...questItems].sort(
        (a, b) => b.fieldData.order - a.fieldData.order,
      );

      // biome-ignore lint/complexity/noForEach: not too big of an array
      sortedCmsQuests.forEach((cmsQuest) => {
        const questType = cmsTypeToQuestType[cmsQuest.fieldData.type];

        if (questType === QuestTileType.FeaturedQuest) {
          response.featuredQuests.push(cmsQuest.fieldData);
        } else if (questType === QuestTileType.MainQuest) {
          response.mainQuests.push(cmsQuest.fieldData);
        } else if (questType === QuestTileType.SideQuest) {
          response.sideQuests.push(cmsQuest.fieldData);
        } else if (questType === QuestTileType.OnboardingQuest) {
          response.onboardingQuests.push(cmsQuest.fieldData);
        } else if (questType === QuestTileType.SeriesQuest) {
          response.seriesQuests.push(cmsQuest.fieldData);
        }

        if (cmsQuest.fieldData["in-game-quest"]) {
          response.inGameQuests.push(cmsQuest.fieldData);
        }
      });

      return {
        featuredQuests: response.featuredQuests,
        mainQuests: response.mainQuests,
        sideQuests: response.sideQuests,
        onboardingQuests: response.onboardingQuests,
        seriesQuests: response.seriesQuests,
        inGameQuests: response.inGameQuests,
      };
    })
    .with({ ok: false }, async (res) => {
      const text = await res.text();
      throw new Error(`Error from API: ${text}`);
    })
    .exhaustive();
};

export async function fetchAllFeedItems<T>(
  url: string,
  fetch: InjectableFetch,
  pageSize = 100, // CMS API default page size
  offset = 0,
  accumulatedItems: Item<T>[] = [],
): Promise<Item<T>[]> {
  const response = await fetch<Response<T>, Error>(
    `${url}${url.includes("?") ? "&" : "?"}offset=${offset}&limit=${pageSize}`,
    {},
  );

  const page = await match(response)
    .with({ ok: true }, async (res) => await res.json())
    .with({ ok: false }, async (res) => {
      throw new Error(`Failed to fetch page: ${await res.text()}`);
    })
    .exhaustive();

  const items = [...accumulatedItems, ...page.items];

  const hasMoreItems =
    page.pagination && offset + pageSize < page.pagination.total;

  if (!hasMoreItems) {
    return items;
  }

  return fetchAllFeedItems<T>(url, fetch, pageSize, offset + pageSize, items);
}

const cmsTypeToQuestType: Record<string, QuestTileType> = {
  dcf30fb508f0f16830d949d9cc303086: QuestTileType.FeaturedQuest,
  b22a4abd20b1f5d232187c877a55492b: QuestTileType.MainQuest,
  ebd4bca5320c99e0b841cd26f9a2154c: QuestTileType.SideQuest,
  a652cd65ff4bd008bd0bc8b8dc4e798e: QuestTileType.OnboardingQuest,
  "17532533be521661970ba4acee471fb9": QuestTileType.SeriesQuest,
};

const cmsAvailableToPlatform: Record<string, Platform> = {
  "6708677b7f6d7402f3959a92": "Mac",
  "670867871d0ca33138507a3e": "Windows",
  "670c4e614dcf738948ec0be4": "iOS",
  "670c4e7908c7315a9e75ca54": "Android",
  "670c4ecb6039543847e13f1d": "BrowserMobile",
  "670c4ee220d4b38fdb8461d1": "BrowserDesktop",
  "670c4ef778df10316f973138": "EpicStore",
  "670c4f0f5947824a1a21005e": "SteamStore",
};

function constructVideoUrl(videoUrl: string | undefined): VideoUrl | undefined {
  if (!videoUrl) {
    return;
  }

  try {
    return new VideoUrl(videoUrl);
  } catch (e) {
    return;
  }
}

export function getQuestFromFieldData(cmsQuestData: FieldDataQuest): Quest {
  return new Quest({
    id: cmsQuestData.slug as RuleId,
    caption: cmsQuestData.name,
    startDate: cmsQuestData["start-date"]
      ? new Date(cmsQuestData["start-date"])
      : undefined,
    endDate: cmsQuestData["end-date"]
      ? new Date(cmsQuestData["end-date"])
      : undefined,
    logo: cmsQuestData.logo?.url
      ? new URL(cmsQuestData.logo.url)
      : "immutable-logo.svg",
    backgroundMediaUrl: cmsQuestData["background-image"]?.url
      ? new URL(cmsQuestData["background-image"].url)
      : "noise.webp",
    backgroundVideoUrl: constructVideoUrl(cmsQuestData["background-video"]),
    gemsAmount: cmsQuestData["gems-amount"]
      ? cmsQuestData["gems-amount"].toString()
      : "",
    analyticsId: cmsQuestData["analytics-id"],
    type: cmsTypeToQuestType[cmsQuestData.type],
    earningType: cmsQuestData.manual
      ? EarningType.OffChain
      : EarningType.OnChain,
    estimateCompletionTime: cmsQuestData["estimated-completion-time"],
    gameUrl: constructUrl(cmsQuestData["game-link"]),
    appleAppStoreUrl: constructUrl(cmsQuestData["apple-store-game-link"]),
    googlePlayUrl: constructUrl(cmsQuestData["google-play-game-link"]),
    layer3Url: constructUrl(cmsQuestData["layer3-link"]),
    registerUrl: constructUrl(cmsQuestData["register-link"]),
    discordUrl: constructUrl(cmsQuestData["discord-link"]),
    supportUrl: cmsQuestData["support-link"]
      ? constructUrl(cmsQuestData["support-link"])
      : constructUrl(cmsQuestData["discord-link"]),
    youtubeUrl: constructUrl(cmsQuestData["youtube-link"]),
    twitterUrl: constructUrl(cmsQuestData["twitter-link"]),
    tradingUrl: constructUrl(cmsQuestData["trading-link"]),
    inGame: cmsQuestData["in-game-quest"],
    quizUrl: constructUrl(cmsQuestData["quiz-link"]),
    verifyEligibilityUrl: constructUrl(cmsQuestData["verify-eligibility-link"]),
    rules: cmsQuestData.rules
      ? {
          items: cmsQuestData.rules.split("\n").map((r) => r.trim()),
        }
      : undefined,
    customTitle: cmsQuestData["custom-title"]
      ? cmsQuestData["custom-title"].split("\n").map((t) => t.trim())
      : undefined,
    customComingSoonTitle: cmsQuestData["custom-coming-soon-title"]
      ? cmsQuestData["custom-coming-soon-title"]
          .split("\n")
          .map((t) => t.trim())
      : undefined,
    subtext: cmsQuestData["sub-text"]
      ? cmsQuestData["sub-text"].split("\n").map((t) => t.trim())
      : undefined,
    gameId: cmsQuestData["game-id"],
    isGemDistributionPending: !cmsQuestData["are-gems-distributed"],
    availableOn: (cmsQuestData["supported-platforms"] || []).map(
      (p) => cmsAvailableToPlatform[p],
    ),
    dedicatedQuestPageId: cmsQuestData["dedicated-quest-page-id"],
    ctaText: cmsQuestData.cta,
    ctaIcon: cmsQuestData["cta-icon"],
  });
}

export const getQuestSections = async (
  fetch: InjectableFetch,
  collectionItemsUrl: URL,
): Promise<QuestSection[]> => {
  const res = await fetch<Response<FieldDataQuestSection>, Error>(
    collectionItemsUrl.toString(),
    {},
  );

  return await match(res)
    .with({ ok: true }, async (res) => {
      const body = await res.json();
      return body.items
        .sort((a, b) => b.fieldData.order - a.fieldData.order)
        .map((i) => ({
          title: i.fieldData.name,
          description: i.fieldData.description,
          questIDs: i.fieldData["quest-ids"]
            ? i.fieldData["quest-ids"].split("\n").map((id) => id.trim())
            : [],
          promoted: i.fieldData.promoted,
          slug: i.fieldData.slug,
        }));
    })
    .with({ ok: false }, async (res) => {
      const text = await res.text();
      throw new Error(`Error from API: ${text}`);
    })
    .exhaustive();
};

export const SUPPORTED_DISCOVER_FEED_ITEM_TYPES: TemplateTypes[] = [
  "video",
  "quiz",
  "cta",
  "followgame",
  "pintohome",
  "allcaughtup",
  "socialconnection",
  "bonusgems",
  "referral",
  "reward",
  "signin",
  "socialquest",
  "selectgenre",
];

const createGetRewardsReadyCard = (): NonNullable<DiscoverFeedItem> => ({
  id: "get-rewards-ready-card",
  type: "getrewardsready",
  priority: -1,
  quest: { questId: "get-rewards-ready", gemsEarnable: 0 },
  bypass: false,
  categories: ["rewards"],
  tags: ["rewards"],
  createdAt: new Date(),
});

export const getDiscoverFeed = async ({
  fetch,
  collectionItemsUrl,
  completedQuestIds,
  discordLinked,
  xLinked,
  shouldShowGetRewardsReadyCard,
  getRewardsReadyInsertionIndex = -1,
  rewardsOnboardingCardFFActive,
}: {
  fetch: InjectableFetch;
  collectionItemsUrl: URL;
  completedQuestIds?: string[];
  discordLinked?: boolean;
  xLinked?: boolean;
  shouldShowGetRewardsReadyCard?: boolean;
  getRewardsReadyInsertionIndex?: number;
  rewardsOnboardingCardFFActive?: boolean;
}) => {
  const rewardsReadyCard = rewardsOnboardingCardFFActive
    ? createGetRewardsReadyCard()
    : undefined;

  if (
    shouldShowGetRewardsReadyCard &&
    getRewardsReadyInsertionIndex >= 0 &&
    rewardsReadyCard
  ) {
    return [rewardsReadyCard];
  }

  const [feedTypes, feedResponse] = await Promise.all([
    getFeedTypes(fetch, appConfig.DISCOVER_FEED_CMS_TYPES_URL).then(
      (types) => ({
        ...types,
      }),
    ),
    fetchAllFeedItems<DiscoverFeedFieldData>(
      collectionItemsUrl.toString(),
      fetch,
    ),
  ]);

  const feed = feedResponse
    .reduce<DiscoverFeedItem[]>((acc, item) => {
      if (item.isArchived) return acc;
      const mappedItem = getDiscoverFeedItemFromFieldData(
        item.fieldData,
        feedTypes,
      );

      // Filter 1: Skip undefined items or bypass items
      if (!mappedItem || mappedItem.bypass) return acc;

      // Filter 3: Mobile-only feed - Don't add pintohome if user is not on mobile
      if (!isMobile() && mappedItem.type === "pintohome") return acc;

      if (!SUPPORTED_DISCOVER_FEED_ITEM_TYPES.includes(mappedItem.type)) {
        notifyError(
          new Error(
            `Unsupported Discover feed item type filtered: ${mappedItem.type}`,
          ),
          "discoverFeedError",
          {
            item: JSON.stringify(mappedItem),
            reason: "filtered_by_supported_types",
          },
        );
        return acc;
      }

      // Filter 7: Completed quest feed
      // Keep item only if it NOT already included in the completed set
      if (
        completedQuestIds &&
        completedQuestIds.length > 0 &&
        isCompletedQuest(completedQuestIds, mappedItem.quest.questId)
      ) {
        return acc;
      }

      if (item.createdOn) mappedItem.createdAt = new Date(item.createdOn);

      // If all filters pass, add the item to the accumulator array
      acc.push(mappedItem);
      return acc;
    }, [])
    .filter((item): item is NonNullable<typeof item> => item !== undefined);

  const mergedFeed = [];

  mergedFeed.push(allCaughtUpCard);

  if (!discordLinked) mergedFeed.push(DiscordConnectionItem);
  if (!xLinked) mergedFeed.push(XConnectionItem);

  mergedFeed.push(...(feed ?? []));

  return mergedFeed
    .filter((f) => !f.bypass)
    .sort((a, b) => a.priority - b.priority);
};

const isCompletedQuest = (
  completedQuestIds: string[] | Set<string>,
  questId: string,
) => {
  // Set for faster lookups
  const questSet =
    completedQuestIds instanceof Set
      ? completedQuestIds
      : new Set(completedQuestIds);

  // Check 1:1 match first
  if (questSet.has(questId)) return true;

  // Check variants
  if (questId.includes("-event") && questSet.has(questId.replace("-event", "")))
    return true;
  return (
    questId.includes("-added") && questSet.has(questId.replace("-added", ""))
  );
};
